import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import { Avatar, Select } from '@lib/components'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { metaActions } from '@store/reducers/manager/meta'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { deleteInvitation, deleteMember } from '@storeOld/reducers/project'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useAuth, useProjectAccess } from '@/hooks'
import { SHARE_PERMISSION } from '@/permissions/project/definitions'
import type { Member as MemberModel } from '@/types/models'
import Api from '../../../../../../../../../../api'
import { PROJECT_MEMBER_LINK_PERMISSION_TYPE } from '../../../ShareOptions/types'
import css from './styles.module.css'

type Props = {
  member: MemberModel
}

enum PROJECT_MEMBER_MANAGE_ACTION_TYPE {
  REMOVE = 'remove',
  CANCEL_INVITATION = 'cancel_invitation',
}

const Member: React.FC<Props> = ({ member }) => {
  const user = useAuth()
  const recipients = useSelector(migrationProjectSelector.getProjectRecipients)
  const dispatch = useDispatch()

  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const isOwner = useSelector(
    migrationProjectSelector.getIsUserOwnerOfProject(member.id)
  )

  const recipient = recipients.find((recipient) => recipient.id === member.id)

  const { hasAccess } = useProjectAccess(
    SHARE_PERMISSION.MANAGE_PROJECT_MEMBERS
  )

  const { hasAccess: hasCopywriterAccess, badgeType: copywriterBadgeType } =
    useProjectAccess(SHARE_PERMISSION.SET_PROJECT_MEMBER_AS_COPYWRITER)

  // There is nothing to comment on or edit in a report, so a person on the
  // list is either looking at it or off it.
  const isReport = useSelector(migrationProjectSelector.getProjectIsReport)

  const reportMemberOptions = [
    [
      {
        label: 'Can view',
        value: PROJECT_MEMBER_LINK_PERMISSION_TYPE.VIEW,
      },
    ],
    [
      {
        label: 'Remove',
        danger: true,
        value: 'remove',
      },
    ],
  ]

  // An invited person is a viewer already, whether or not they have opened the
  // link yet — the row says so and offers the one thing left to do.
  const reportInviteOptions = [
    [
      {
        label: 'Can view',
        value: PROJECT_MEMBER_LINK_PERMISSION_TYPE.VIEW,
        disabled: true,
      },
    ],
    [
      {
        label: 'Cancel invitation',
        danger: true,
        value: PROJECT_MEMBER_MANAGE_ACTION_TYPE.CANCEL_INVITATION,
      },
    ],
  ]

  const memberOptions = [
    [
      {
        label: 'Viewer',
        value: PROJECT_MEMBER_LINK_PERMISSION_TYPE.VIEW,
      },
      {
        label: 'Commenter',
        value: PROJECT_MEMBER_LINK_PERMISSION_TYPE.COMMENT,
      },
      {
        label: 'Copywriter',
        value: PROJECT_MEMBER_LINK_PERMISSION_TYPE.EDIT_CONTENT,
        disabled: !hasCopywriterAccess,
        badgeType: copywriterBadgeType,
      },
      {
        label: 'Editor',
        value: PROJECT_MEMBER_LINK_PERMISSION_TYPE.EDIT,
      },
    ],
    [
      {
        label: 'Remove',
        danger: true,
        value: 'remove',
      },
    ],
  ]

  const inviteOptions = [
    {
      label: 'Cancel invitation',
      danger: true,
      value: PROJECT_MEMBER_MANAGE_ACTION_TYPE.CANCEL_INVITATION,
    },
  ]

  const handleChangeMemberPermission = (
    value:
      | PROJECT_MEMBER_LINK_PERMISSION_TYPE
      | PROJECT_MEMBER_MANAGE_ACTION_TYPE
      | string
  ) => {
    const isCopywriterRestricted =
      value === PROJECT_MEMBER_LINK_PERMISSION_TYPE.EDIT_CONTENT &&
      !hasCopywriterAccess

    const shouldSuggestTeamUpgrade =
      isCopywriterRestricted && copywriterBadgeType === 'plan_team'

    if (shouldSuggestTeamUpgrade) {
      sendGtmEvent(GtmTriggerName.UPGRADE_GUEST_ONLY)

      dispatch(
        openModal({
          modalType: MODAL_UPGRADE_PLAN,
          modalProps: {
            planType: 'team',
            slideType: 'comments',
          },
        })
      )
    }

    if (isCopywriterRestricted) {
      return
    }

    // @TODO review this and fix
    // @ts-expect-error - Argument of type 'string' is not assignable to parameter of type 'PROJECT_MEMBER_LINK_PERMISSION_TYPE'
    if (Object.values(PROJECT_MEMBER_LINK_PERMISSION_TYPE).includes(value)) {
      dispatch(
        metaActions.updateMemberPermission({
          memberId: member.id,
          permission: value,
        })
      )

      if (value === PROJECT_MEMBER_LINK_PERMISSION_TYPE.EDIT_CONTENT) {
        sendGtmEvent(GtmTriggerName.PROJECT_COPYWRITER)
      }

      Api.Projects.memberPermission({
        projectId,
        memberId: member.id,
        permission: value,
      })
        .then(() => {
          dispatch(
            notificationActionCreators.setNotification({
              type: NotificationType.ACCESS_SETTINGS_UPDATED,
            })
          )
        })
        .catch((err) => {
          // biome-ignore lint/suspicious/noConsole: intentional
          console.log('err', err)
        })
    } else {
      if (value === PROJECT_MEMBER_MANAGE_ACTION_TYPE.REMOVE) {
        dispatch(
          // @todo implement action
          // @ts-expect-error
          deleteMember({
            projectId,
            id: member.id,
          })
        )
      }

      if (value === PROJECT_MEMBER_MANAGE_ACTION_TYPE.CANCEL_INVITATION) {
        // @todo implement action
        // @ts-expect-error
        dispatch(deleteInvitation(member.id))
      }
    }
  }

  return (
    <div className={css.container}>
      <div className={css.member} data-testid="member_block">
        <div className={css.left}>
          <div className={css.avatar} data-testid="member_avatar">
            {recipient && recipient.avatar && (
              <Avatar me={member.id === user.id} media={recipient.avatar} />
            )}

            {(!recipient || !recipient.avatar) && (
              <Avatar
                me={member.id === user.id}
                color={member.color}
                children={member.fullName}
              />
            )}
          </div>

          <div className={css.body}>
            <div className={css.name} data-testid="member_name">
              {!member.pending ? member.fullName : 'Pending...'}
            </div>

            <div className={css.email} data-testid="member_email">
              {member.emailAddress}
            </div>
          </div>
        </div>

        {isOwner && <div className={css.owner_label}>Owner</div>}

        {!isOwner && (
          <Select
            type="select"
            selected={
              isReport
                ? PROJECT_MEMBER_LINK_PERMISSION_TYPE.VIEW
                : member.permission
            }
            placeholder={member.pending && !isReport ? 'Pending' : undefined}
            options={
              isReport
                ? member.pending
                  ? reportInviteOptions
                  : reportMemberOptions
                : member.pending
                  ? inviteOptions
                  : memberOptions
            }
            disabled={!hasAccess}
            onChange={(value) => handleChangeMemberPermission(value as string)}
            position="bottom-end"
          />
        )}

        {/* <Role member={member} /> */}
      </div>
    </div>
  )
}

export default Member
